import { useMemo } from 'react';

import { Switch } from '@mui/material';
import { GridColDef, GridRowsProp } from '@mui/x-data-grid-pro';

import { EditRemoveBtn } from 'components/buttons';
import { PanelGrid } from 'components/grid';
import { Block } from 'components/page';
import { useFormMutation } from 'mutations/useFormMutation';
import { ConfigPedidoSituacaoColumn } from 'pages/compra/configuracao-pedido/components';
import { useQueryConfigPedido } from 'pages/compra/configuracao-pedido/hooks';
import { ConfiguracaoPedidoCompraList } from 'pages/compra/configuracao-pedido/types';
import { brlDate } from 'util/format';

/**
 * Gera as linhas da grid.
 */
const getRows = (data: ConfiguracaoPedidoCompraList[] = []): GridRowsProp =>
    data.map((item) => ({
        id: item.idConfigPedido,
        idConfigPedido: item.idConfigPedido,
        dsConfigPedido: item.dsConfigPedido,
        dtInicioVigencia: item.dtInicioVigencia,
        dtFimVigencia: item.dtFimVigencia,
        dsTipoGrade: item.tipoGrade.dsTipoGrade,
        dsTipoPedido: item.tipoPedido.dsTipoPedido,
        fgAtivo: item.fgAtivo,
        fgVisivel: item.fgVisivel,
        idConfigPedidoStatus: item.idConfigPedidoStatus,
    }));

export function ConfigPedidoGrid() {
    const { mutate } = useFormMutation();
    const { data, isFetching, refetch } = useQueryConfigPedido();
    const rows = useMemo(() => getRows(data?.data), [data]);

    const columns: GridColDef[] = useMemo(
        () => [
            { field: 'idConfigPedido', headerName: 'Código' },
            { field: 'dsConfigPedido', headerName: 'Configuração', type: 'string', minWidth: 120, flex: 3 },
            { field: 'dsTipoPedido', headerName: 'Tipo de Pedido', type: 'string', minWidth: 120, flex: 1 },
            { field: 'dsTipoGrade', headerName: 'Grade', type: 'string', minWidth: 120, flex: 1 },
            {
                field: 'dtInicioVigencia',
                headerName: 'Início de Vigência',
                type: 'string',
                align: 'center',
                headerAlign: 'center',
                valueFormatter: (value) => brlDate(`${value}T00:00:00`),
                minWidth: 120,
                flex: 1,
            },
            {
                field: 'dtFimVigencia',
                headerName: 'Fim de Vigência',
                type: 'string',
                align: 'center',
                headerAlign: 'center',
                valueFormatter: (value) => brlDate(`${value}T00:00:00`),
                minWidth: 120,
                flex: 1,
            },
            {
                field: 'idConfigPedidoStatus',
                headerName: 'Situação',
                headerAlign: 'center',
                minWidth: 140,
                renderCell: ({ row }) => <ConfigPedidoSituacaoColumn idStatus={row.idConfigPedidoStatus} />,
            },
            {
                field: 'visivel',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Visível',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                minWidth: 70,
                renderCell: ({ row }) => (
                    <Switch
                        checked={row.fgVisivel}
                        onChange={(e) => {
                            row.fgVisivel = e.target.checked;

                            mutate({
                                url: `/gestao/configpedido/${row.idConfigPedido}/visivel`,
                                method: 'PUT',
                                formData: { fgVisivel: e.target.checked },
                            });
                        }}
                    />
                ),
            },
            {
                field: 'status',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Status',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                minWidth: 70,
                renderCell: ({ row }) => (
                    <Switch
                        checked={row.fgAtivo}
                        onChange={(e) => {
                            row.fgAtivo = e.target.checked;

                            mutate({
                                url: `/gestao/configpedido/${row.idConfigPedido}/ativo`,
                                method: 'PUT',
                                formData: { fgAtivo: e.target.checked },
                            });
                        }}
                    />
                ),
            },
            {
                field: 'editar',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Ações',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                minWidth: 100,
                renderCell: ({ row }) => (
                    <EditRemoveBtn
                        id={row.idConfigPedido}
                        refresh={refetch}
                        editUrl="/compra/configuracao-pedido/form/"
                        removeUrl={`/gestao/configpedido/${row.idConfigPedido}`}
                        showRemoveBtn={true}
                        cloneUrl={`/gestao/configpedido/${row.idConfigPedido}/clonar`}
                    />
                ),
            },
        ],
        [],
    );

    return (
        <Block>
            <PanelGrid rows={rows} rowsCount={data?.total ?? 0} reload={refetch} loading={isFetching} columns={columns} />
        </Block>
    );
}
